/* eslint-disable no-param-reassign */

import useAsyncComputed, { useAsyncComputedAsReactive } from '@/compositions/useAsyncComputed.js';
import { resolveEntryMutating, getEntriesByKey, getEntriesByModelSlug, getContentfulComponentDependencies } from '@/services/ContentfulService.js';
import { reactive, toRefs, watch, computed } from 'vue';
import Tracking from '@/components/commons/Tracking.js';

export const contentfulProps = {
  fields: {
    type: Object,
    default: null,
  },
  sys: {
    type: Object,
    default() {
      return {};
    },
  },
  slug: {
    type: String,
    default: null,
  },
};

const state = reactive({
  isPreview: false,
  currentEntry: null,
});

export function setContentfulState(vals) {
  Object.assign(state, vals);
  return state;
}
export function getContentfulState() {
  return state;
}

const loadedDependencies = reactive({
  moduleNames: [],
});

export function getLoadedDependencies() {
  return loadedDependencies;
}

function updateDependencies(newDependencies) {
  const filteredDependencies = newDependencies.filter((d) => !loadedDependencies.moduleNames.includes(d));
  if (filteredDependencies.length !== 0) {
    loadedDependencies.moduleNames = [...loadedDependencies.moduleNames, ...filteredDependencies];
  }
}

export function useDynamicText(slug, _default) {
  const [data, loading, init] = useAsyncComputed(async (prev) => {
    const result = await getEntriesByModelSlug('dynamicText', slug, 2);
    return result?.fields?.text || _default;
  }, _default);
  return {
    data,
    loading,
    init,
  };
}

const fallbackResponse = ()=>{
  return {
    data: computed(()=>undefined),
    loading: false,
    loadedDependencies,
    ...toRefs(state),
    resolveEntry: (entry, depth) => resolveEntryMutating(entry, depth, state.isPreview),
    init : ()=>undefined
  }
}

export function useContentful(contentTypeId, props, ctx = {}, opts = {}) {
  if (!props.sys?.id && !props.slug) {
    // this happens when contentful entry is in invalid state.
    // usually in preview links while page is being worked on
    if (!state.isPreview) {
      const e = new Error(`contentful: Either sys.id or slug field is required. typeId: ${contentTypeId}`);
      Tracking.sentryCaptureError(e);
      console.error(e);
    }

    return fallbackResponse();
  }

  const options = { resolveDepth: 2, errorPolicy: 'none', ...opts };

  const initialData = reactive({ data: props.fields, system: props.sys });

  const [data, loading, init] = useAsyncComputedAsReactive(async (prev) => {
    if (props.fields) {
      const dependencies = getContentfulComponentDependencies(contentTypeId, props.fields?.component ?? '');
      if (dependencies.length !== 0) {
        updateDependencies(dependencies);
      }
      prev.data = props.fields;
      prev.system = props.sys;
      return;
    }
    try {
      let result;
      if (props.sys?.id) {
        result = await getEntriesByKey(props.sys.id, props.sys.linkType === 'Asset', options.resolveDepth, state.isPreview);
        if (!result) {
          if (opts.errorPolicy === 'ignore') {
            return;
          }
          throw new Error(
            `contentful request returned unexpected null ${props.sys?.id}/${contentTypeId}/${props.slug} \n This is USER ERROR from Contentful Dashboard`,
          );
        }
        if (result?.sys?.id !== props.sys.id) {
          throw new Error(`contentful response id did not match request id ${props.sys?.id}/${contentTypeId}/${props.slug}`);
        }
      } else if (props.slug && contentTypeId) {
        result = await getEntriesByModelSlug(contentTypeId, props.slug, options.resolveDepth, state.isPreview, options.errorPolicy);
      } else {
        throw new Error(`contentful: vue instance without fields, sys, slug, or contentTypeId`);
      }
      if (!result) {
        if (opts.errorPolicy === 'ignore') {
          return;
        }
        throw new Error(`contentful request returned unexpected null ${props.sys?.id}/${contentTypeId}/${props.slug}`);
      }

      if (!result?.fields) {
        throw new Error(`contentful response does not contain fields ${props.sys?.id}/${contentTypeId}/${props.slug}`);
      }
      const dependencies = getContentfulComponentDependencies(contentTypeId, result?.fields?.component ?? '');
      if (dependencies.length !== 0) {
        updateDependencies(dependencies);
      }
      prev.data = result.fields;
      prev.system = result.sys;
    } catch (e) {
      if (!state.isPreview) {
        Tracking.sentryCaptureError(e);
        console.error(e);
      }
      return fallbackResponse();
    }
  }, initialData);

  watch(data, () => {
    if(ctx && ctx.emit) {
      ctx.emit('updated', data)
    }
  });

  const resolveEntry = (entry, depth) => resolveEntryMutating(entry, depth, state.isPreview);

  return {
    ...toRefs(data),
    loading,
    loadedDependencies,
    ...toRefs(state),
    resolveEntry,
    init
  };
}
